import { Fragment, useState } from 'react';

import { ArrowDropDown, ArrowDropUp, Close, Menu } from '@mui/icons-material';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  Hidden,
  IconButton,
  Typography,
} from '@mui/material';
import kebabcase from 'lodash/kebabCase';
import { useHistory, useParams } from 'react-router-dom';

import logo from '../../../../../assets/imgs/logo.svg';
import {
  colors,
  MARKET_ICONS,
  markets,
  sections,
  transition,
} from '../../constants';

interface YearbookParams {
  year: string;
  market?: string;
  section?: string;
}

export function MobileMenu() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const { year, market } = useParams<YearbookParams>();
  const history = useHistory();

  const currentMarket = markets.find((m) => m === market) ?? markets[0];

  const handleNavigate = (path: string) => {
    history.push(`/anuarios/${year}/${currentMarket}/${path}`);
    setDrawerOpen(false);
    setExpandedSection(null);
  };

  const handleMarketSelect = (newMarket: string) => {
    history.push(`/anuarios/${year}/${newMarket}/inicio`);
    setDrawerOpen(false);
  };

  const toggleSection = (title: string) => {
    setExpandedSection((prev) => (prev === title ? null : title));
  };

  return (
    <Hidden lgUp>
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1300,
        }}>
        <IconButton
          aria-label="abrir menu"
          onClick={() => setDrawerOpen(true)}
          sx={{
            display: drawerOpen ? 'none' : 'flex',
            backgroundColor: 'rgba(14, 15, 14, 0.85)',
            backdropFilter: 'blur(10px)',
            border: '1.5px solid',
            borderColor: colors.orange,
            color: colors.white,
            width: 46,
            height: 46,
            transition,
            '&:hover': {
              backgroundColor: colors.orange,
              color: colors.white,
              transform: 'scale(1.05)',
            },
          }}>
          <Menu fontSize="small" />
        </IconButton>
      </Box>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '75vw', sm: 300 },
            backgroundColor: '#0E0F0E',
            borderRight: '1px solid rgba(255, 130, 17, 0.25)',
            boxShadow: '-8px 0 40px rgba(0, 0, 0, 0.6)',
            display: 'flex',
            flexDirection: 'column',
            pt: 3,
            pb: 4,
            px: 3,
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}>
          <Box
            component="img"
            src={logo}
            height={36}
            alt="Logo Uqbar"
            sx={{ cursor: 'pointer' }}
            onClick={() => handleNavigate('inicio')}
          />
          <IconButton
            aria-label="fechar menu"
            onClick={() => setDrawerOpen(false)}
            sx={{
              color: colors.white,
              transition,
              '&:hover': { color: colors.orange, transform: 'rotate(90deg)' },
            }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 130, 17, 0.2)', mb: 2 }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            flex: 1,
            overflowY: 'auto',
          }}>
          {sections.map((section) => {
            const hasSubsections = Boolean(section?.subsections);
            const isExpanded = expandedSection === section.title;

            return (
              <Fragment key={section.title}>
                <Box
                  onClick={() => {
                    if (hasSubsections) {
                      toggleSection(section.title);
                    } else {
                      handleNavigate(kebabcase(section.title));
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 1.25,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    color: colors.white,
                    transition,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 130, 17, 0.08)',
                      color: colors.orange,
                    },
                  }}>
                  <Typography
                    fontFamily="Figtree"
                    fontSize="1rem"
                    fontWeight={500}>
                    {section.title}
                  </Typography>
                  {hasSubsections &&
                    (isExpanded ? (
                      <ArrowDropUp sx={{ color: colors.orange }} />
                    ) : (
                      <ArrowDropDown sx={{ color: colors.text }} />
                    ))}
                </Box>

                {hasSubsections && (
                  <Collapse in={isExpanded} unmountOnExit>
                    <Box
                      sx={{
                        pl: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.25,
                        mb: 0.5,
                      }}>
                      {(section.subsections ?? []).map((sub) => (
                        <Typography
                          key={sub.title}
                          fontFamily="Figtree"
                          fontSize="0.9rem"
                          onClick={() =>
                            handleNavigate(
                              kebabcase(`${section.title} ${sub.title}`),
                            )
                          }
                          sx={{
                            px: 1.5,
                            py: 1,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: colors.text,
                            transition,
                            '&:hover': {
                              color: colors.orange,
                              backgroundColor: 'rgba(255, 130, 17, 0.06)',
                            },
                          }}>
                          {sub.title}
                        </Typography>
                      ))}
                    </Box>
                  </Collapse>
                )}
              </Fragment>
            );
          })}
        </Box>

        <Divider
          sx={{ borderColor: 'rgba(255, 130, 17, 0.2)', mt: 2, mb: 3 }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography
            fontFamily="Figtree"
            fontSize="0.7rem"
            sx={{
              color: colors.textDim,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              px: 1.5,
            }}>
            Mercado
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 1 }}>
            {markets.map((m) => {
              const isSelected = m === currentMarket;
              const Icon = MARKET_ICONS[m];
              return (
                <Box
                  key={m}
                  onClick={() => handleMarketSelect(m)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    minWidth: '48px',
                    gap: 0.5,
                    px: 0.5,
                    py: 0.75,
                    borderRadius: 1,
                    border: `1.5px solid ${
                      isSelected ? colors.orange : 'rgba(255,255,255,0.15)'
                    }`,
                    backgroundColor: isSelected
                      ? 'rgba(255, 130, 17, 0.1)'
                      : 'transparent',
                    color: isSelected ? colors.orange : colors.white,
                    cursor: 'pointer',
                    fontFamily: 'Figtree',
                    fontSize: '0.65rem',
                    fontWeight: isSelected ? 600 : 400,
                    transition,
                    '&:hover': {
                      borderColor: colors.orange,
                      color: colors.orange,
                      backgroundColor: 'rgba(255, 130, 17, 0.08)',
                    },
                  }}>
                  {Icon && <Icon sx={{ fontSize: '1rem' }} />}
                  {m}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Drawer>
    </Hidden>
  );
}
